import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type {
	IDexfSettings,
	IFailedShoppingItem,
	ILocalizedInformation,
	IShoppingProduct,
	ITheme
} from '@inter-ikea-kompis/types';
import { DexfSettingsUtility, ShoppingSupport } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';
import type { IKompisElementPropertyValue } from '@inter-ikea-kompis/web-component';

import {
	Accordion,
	AccordionItem,
	AccordionItemPadding
} from '@inter-ikea-kompis/component-accordion';
import { ProductRow } from '@inter-ikea-kompis/component-product-row';
import { Text } from '@inter-ikea-kompis/component-text';

import { CheckboxGroup as SkapaCheckboxGroup } from '@ingka/checkbox-webc/passive.js';

import type IAddToBagTranslations from '../../types/IAddToBagTranslations.js';
import UnavailableProductsCardStyle from './UnavailableProductsCardStyle.js';

/**
 * Card to notify users that some items are not sold online.
 *
 * @event addToShoppingList Emits when checking/unchecking the checkbox for adding unavailable items to the shopping list
 */

export default class UnavailableProductsCard extends KompisElement {
	// Theme
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IAddToBagTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IDexfSettings | null = null;

	// Shopping products
	@property({ type: Object })
	public shoppingProducts: IShoppingProduct[] | null = null;

	// Add unavailable items to shopping list
	@property({ type: Boolean })
	public addToShoppingList = false;

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	// List of failed shopping products returned when trying to add to cart.
	@property({ type: Object })
	public failedShoppingItems: IFailedShoppingItem[] | null = null;

	// If the accordion with unavailable products list is expanded
	private expandedList = false;
	private dexfSettings: IDexfSettings | null = null;

	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<style>
				${UnavailableProductsCardStyle.getStyle({ theme: this.theme })}
			</style>

			<div class="card">
				${this.getCheckbox()} ${this.getHelperText()} ${this.getUnavailableProductsAccordion()}
			</div>
		`;
	}

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-checkbox-group', SkapaCheckboxGroup);
		this.customElements.define('kompis-product-row', ProductRow);
		this.customElements.define('kompis-accordion', Accordion);
		this.customElements.define('kompis-accordion-item', AccordionItem);
		this.customElements.define('kompis-accordion-item-padding', AccordionItemPadding);
		this.customElements.define('kompis-text', Text);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, IKompisElementPropertyValue>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}
		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}

		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getSettings(): IDexfSettings {
		return <IDexfSettings>this.dexfSettings;
	}

	private getTranslations(): IAddToBagTranslations {
		const translations = this.translations
			? this.translations
			: <IAddToBagTranslations>this.localizedInformation?.translations;

		return translations;
	}
	private getHelperText(): RenderResult {
		const {
			addToShoppingCartUnavailableDialogueNoListHelpText,
			addToShoppingCartUnavailableDialogueListHelpText
		} = this.getTranslations();

		if (!ShoppingSupport.hasListSupport(this.getSettings())) {
			return html`
				<div class="help">
					<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}" bold>
						${addToShoppingCartUnavailableDialogueNoListHelpText}
					</kompis-text>
				</div>
			`;
		}

		return html` <div class="help">
			<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}">
				${addToShoppingCartUnavailableDialogueListHelpText}
			</kompis-text>
			<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}" bold>
				${addToShoppingCartUnavailableDialogueNoListHelpText}
			</kompis-text>
		</div>`;
	}

	private getCheckbox(): RenderResult {
		if (!ShoppingSupport.hasListSupport(this.getSettings())) {
			return null;
		}
		const addToShoppingCartUnavailableDialogueOption =
			this.getTranslations().addToShoppingCartUnavailableDialogueOption;

		return html`
			<skapa-checkbox-group>
				<input
					type="checkbox"
					aria-checked="${this.addToShoppingList}"
					id="unavailableproducts"
					.checked="${this.addToShoppingList}"
					@change="${() =>
						this.dispatchEvent(
							new CustomEvent('addToShoppingList', {
								bubbles: true,
								detail: {
									addToShoppingList: !this.addToShoppingList
								}
							})
						)}"
				/>
				<label class="checkboxLabel" for="unavailableproducts">
					${addToShoppingCartUnavailableDialogueOption}
				</label>
			</skapa-checkbox-group>
		`;
	}

	private getA11yList(): RenderResult {
		/* eslint-disable lit-a11y/no-redundant-role */
		return html`
			<ul class="unavailable-list" role="list">
				${this.getUnavailableShoppingProducts().map((shoppingProduct) =>
					this.getRow(shoppingProduct)
				)}
			</ul>
		`;
		/* eslint-enable lit-a11y/no-redundant-role */
	}

	private getExpandableLabel(): string {
		const addToShoppingCartUnavailableDialogueUnavailableItems =
			this.getTranslations().addToShoppingCartUnavailableDialogueUnavailableItems;

		return `${addToShoppingCartUnavailableDialogueUnavailableItems} (${this.getUnavailableShoppingProducts().reduce(
			(sum, product) => sum + product.quantity,
			0
		)})`;
	}

	private toggleExpanded(): void {
		this.expandedList = !this.expandedList;
		this.requestUpdate();
	}

	private getUnavailableProductsAccordion(): RenderResult {
		return html`
			<kompis-accordion .theme="${this.theme}" noHorizontalPadding>
				<kompis-accordion-item
					@expand="${() => {
						this.toggleExpanded();
					}}"
					.theme="${this.theme}"
					.label="${this.getExpandableLabel()}"
					.expanded="${this.expandedList}"
					noHorizontalPadding
				>
					${this.getA11yList()}
				</kompis-accordion-item>
			</kompis-accordion>
		`;
	}

	private getUnavailableShoppingProducts(): IShoppingProduct[] {
		if ((this.failedShoppingItems?.length ?? 0) > 0) {
			const failedShoppingItems = this.getFailedShoppingItems(this.shoppingProducts);
			return failedShoppingItems;
		}

		return (
			this.shoppingProducts
				?.filter((shoppingProduct) => shoppingProduct.quantity > 0)
				.filter(
					(shoppingProduct) =>
						!shoppingProduct.product.content?.priceInformation?.availableForHomeDelivery &&
						!shoppingProduct.product.content?.priceInformation?.availableForClickCollect
				) ?? []
		);
	}

	/**
	 * Returns ShoppingProducts that has a reference in FailedShoppingItems.
	 *
	 * @param shoppingProducts Shopping products.
	 * @returns List on unavailable shopping products.
	 */
	private getFailedShoppingItems(shoppingProducts: IShoppingProduct[] | null): IShoppingProduct[] {
		return shoppingProducts?.filter((product) => this.isFailedShoppingItem(product)) ?? [];
	}

	/**
	 * Validates if a shopping product is present in the failed shopping items.
	 *
	 * @param shoppingProduct Single shopping product.
	 * @returns Boolean.
	 */
	private isFailedShoppingItem(shoppingProduct: IShoppingProduct): boolean {
		const foundFailedShoppingItem = this.failedShoppingItems?.find((failedShoppingProduct) => {
			const firstCheck = failedShoppingProduct.itemNo === shoppingProduct.product.content?.ruItemNo;
			const secondCheck =
				failedShoppingProduct.itemNo === shoppingProduct.product.content?.itemNoLocal;
			return firstCheck || secondCheck;
		});

		if (foundFailedShoppingItem) {
			return true;
		}
		return false;
	}

	private getRow(shoppingProduct: IShoppingProduct): RenderResult {
		return html`
			<div class="productRowContainer">
				<kompis-product-row
					.theme="${this.theme}"
					.disableLinks="${this.disableLinks}"
					.localizedInformation="${this.localizedInformation}"
					.settings="${this.settings}"
					.translations="${this.translations}"
					.shoppingProduct="${shoppingProduct}"
					showItemNo
				></kompis-product-row>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-checkbox-group': SkapaCheckboxGroup;
		'kompis-unavailable-products-card': UnavailableProductsCard;
	}
}
